import React from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { rem } from 'polished'
import { Moment } from 'moment'

import SlideDown from 'components/Common/SlideDown'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import removeSpace from 'lib/string/removeSpaceUtils'
import { getItineraryItems } from 'selectors/bookingSidebarSelectors'
import LinePlaneArrivalIcon from 'components/Luxkit/Icons/line/LinePlaneArrivalIcon'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import CheckInIcon from 'components/Luxkit/Icons/custom/CheckInIcon'
import CheckOutIcon from 'components/Luxkit/Icons/custom/CheckOutIcon'
import TourStartIcon from 'components/Luxkit/Icons/custom/TourStartIcon'
import TourEndIcon from 'components/Luxkit/Icons/custom/TourEndIcon'

import { DATE_TIME_FORMAT, ACCOMMODATION_TIME_FORMAT } from 'constants/dateFormats'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import SectionMessage from 'components/Common/SectionMessage'
import { getTotalWithFee } from 'selectors/bookingSidebarSelectors'
import FormatCurrency from 'components/Common/FormatCurrency'
import { isChangePackage } from 'selectors/cart/cartSelectors'

export enum ItineraryItemTypes {
  FlightDeparture,
  FlightArrival,
  StopOver,
  HotelCheckIn,
  HotelCheckOut,
  TourStart,
  TourEnd,
}

export interface ItineraryItem {
  id: string;
  type: ItineraryItemTypes;
  when: Moment;
  location?: string;
  image?: {
    url: string;
    alt: string;
  };
}

interface Props {
  itineraryItems: Array<ItineraryItem>;
  defaultClosed?: boolean;
  totalWithFee?: number;
  isChangeDates: boolean;
  isDepositOrder: boolean;
  isChangePackage: boolean;
}

interface State {
  open?: boolean;
}

const ItineraryItemContainer = styled.div`
  display: flex;
  margin-top: ${rem(18)};
  position: relative;
  align-items: center;
`

const ItineraryStopOverItemContainer = styled(ItineraryItemContainer)`
  margin-bottom: ${rem(18)};
  align-items: center;
`

const IterinaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const IterinaryJoiner = styled.div`
  position: absolute;
  width: 2px;
  background-color: ${props => props.theme.palette.neutral.default.five};
  height: ${rem(18)};
  top: -${rem(18)};
  left: 11px;
`

const ItineraryStopOverDot = styled.div`
  height: 20px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    height: ${rem(8)};
    width: ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.round};
    background-color: ${props => props.theme.palette.neutral.default.one};
  }
`

const ItineraryItemLabel = styled(Caption)`
  margin-left: ${rem(16)};
`

const AirlineLogo = styled.img`
  width: ${rem(54)};
  margin-left: auto;
  align-self: center;
`

const StyledSectionMessage = styled(SectionMessage)`
  border-radius: ${props => props.theme.borderRadius.S};
  margin-top: ${rem(16)};
`

const TextContent = styled.div`
  margin-left: ${rem(12)};
  text-align: center;
  color: ${({ theme }) => theme.palette.brand.primary.normal};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  * + * {
    margin-top: ${rem(4)};
  }
`

function getDateFormat(type: ItineraryItemTypes) {
  switch (type) {
    case ItineraryItemTypes.HotelCheckIn:
    case ItineraryItemTypes.HotelCheckOut:
    case ItineraryItemTypes.TourStart:
    case ItineraryItemTypes.TourEnd:
      return ACCOMMODATION_TIME_FORMAT
    default:
      return DATE_TIME_FORMAT
  }
}

function getTypeConfig(type: ItineraryItemTypes): { label: string; icon: React.ReactChild; } {
  switch (type) {
    case ItineraryItemTypes.FlightArrival:
      return { label: 'Arrive', icon: <LinePlaneArrivalIcon /> }
    case ItineraryItemTypes.FlightDeparture:
      return { label: 'Depart', icon: <LinePlaneFlyIcon /> }
    case ItineraryItemTypes.HotelCheckIn:
      return { label: 'Check In', icon: <CheckInIcon /> }
    case ItineraryItemTypes.HotelCheckOut:
      return { label: 'Check Out', icon: <CheckOutIcon /> }
    case ItineraryItemTypes.TourStart:
      return { label: 'Start', icon: <TourStartIcon /> }
    case ItineraryItemTypes.TourEnd:
      return { label: 'End', icon: <TourEndIcon /> }
  }
}

class BookingSidebarItinerary extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    open: !this.props.defaultClosed,
  }

  onToggleOpen = () => {
    this.setState(prevState => ({ open: !prevState.open }))
  }

  render() {
    const { itineraryItems, totalWithFee, isChangeDates, isDepositOrder, isChangePackage } = this.props
    const { open } = this.state
    const refundSum = totalWithFee * -1
    return (
      <div>
        <IterinaryHeader data-testid="itinerary-header">
          <BodyText variant="large" weight="bold" as="h3">Your Itinerary</BodyText>
          <SmallToggleButton
            on={open}
            onClick={this.onToggleOpen}
          >
            <BodyText variant="medium">{open ? 'Hide' : 'Show'}</BodyText>
          </SmallToggleButton>
        </IterinaryHeader>
        <SlideDown show={open}>
          {itineraryItems.map((item, index) => {
            const typeConfig = getTypeConfig(item.type)
            if (item.type === ItineraryItemTypes.StopOver) {
              return (
                <ItineraryStopOverItemContainer key={item.id}>
                  <IterinaryJoiner />
                  <ItineraryStopOverDot />
                  <ItineraryItemLabel variant="medium">
                    <b>Stop Over</b> in {item.location}
                  </ItineraryItemLabel>
                </ItineraryStopOverItemContainer>
              )
            }

            return (
              <ItineraryItemContainer key={item.id} data-testid={`itinerary-${removeSpace(typeConfig.label)}`}>
                {index !== 0 && <IterinaryJoiner />}
                {typeConfig.icon}
                <ItineraryItemLabel variant="large">
                  <strong>
                    {typeConfig.label}
                    {item.location ? ` ${item.location}` : ''}
                  </strong><br />
                  {item.when.format(getDateFormat(item.type))}
                </ItineraryItemLabel>
                {item.image && <AirlineLogo
                  src={item.image.url}
                  alt={item.image.alt}
                />}
              </ItineraryItemContainer>
            )
          })}
        </SlideDown>
        {isChangeDates && !isChangePackage && refundSum > 0 &&
          <StyledSectionMessage type="success-no-border">
            <TextContent>
              Your new travel dates are <FormatCurrency value={refundSum} /> cheaper !
              <br/>
              <br/>
              {isDepositOrder ?
                'This will be discounted from the upcoming payment for the remainder of your booking.' :
                'You will be refunded this amount to your account in credits to use on another escape.'
                }
            </TextContent>
          </StyledSectionMessage>
        }
      </div>
    )
  }
}

function mapStateToProps(state: App.State) {
  return {
    itineraryItems: getItineraryItems(state),
    totalWithFee: getTotalWithFee(state),
    isChangeDates: state.cart.isChangeDates,
    isDepositOrder: state.order?.order?.payments?.[0].depositDetails != null,
    isChangePackage: isChangePackage(state),
  }
}

export default connect(mapStateToProps)(BookingSidebarItinerary)
